<template>
  <div class="input-container" :class="variantClass">
    <legend>{{ legend }}</legend>

    <TheMask v-if="mask" class="input" :mask="mask" :value="modelValue" @input="$emit('update:modelValue', $event)"
      :placeholder="placeholder" :disabled="disabled" />

    <div v-else-if="type === 'password'" class="password-container">
      <input class="input" :type="isPasswordVisible ? 'text' : 'password'" :value="modelValue"
        @input="$emit('update:modelValue', $event.target.value)" :placeholder="placeholder" :disabled="disabled"
        autocomplete="off" />

      <button type="button" @click="togglePasswordVisibility">
        <EyeOutline :size="18" v-if="!isPasswordVisible" />
        <EyeOffOutline :size="18" v-else />
      </button>
    </div>

    <div v-else-if="type === 'currency'" class="currency-container">
      <span class="currency-prefix">R$</span>

      <input class="input" type="text" :value="formattedValue" @input="handleCurrencyInput($event)"
        @keydown="handleKeyDown($event)" :placeholder="placeholder" :disabled="disabled" :readonly="readonly" />
    </div>

    <input v-else :type="type" class="input" :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)" :placeholder="placeholder" :disabled="disabled"
      :readonly="readonly" autocomplete="off">
  </div>
</template>

<script>
import EyeOffOutline from 'icons/EyeOffOutline'
import EyeOutline from 'icons/EyeOutline'
import { TheMask } from 'vue-the-mask'

export default {
  name: 'Input',
  components: {
    TheMask,
    EyeOutline,
    EyeOffOutline,
  },
  props: {
    type: {
      type: String,
      default: 'text',
    },
    mask: {
      type: [String, Array],
    },
    legend: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    modelValue: {
      required: true,
    },
    variant: {
      type: String,
      default: 'default',
    },
  },
  data() {
    return {
      isPasswordVisible: false,
    }
  },
  computed: {
    variantClass() {
      return this.variant === 'dark' ? 'variant-dark' : 'default'
    },
    formattedValue() {
      const value = this.modelValue || 0

      return (value / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
  methods: {
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible
    },
    handleCurrencyInput(event) {
      const input = event.target.value

      const numericValue = input.replace(/\D/g, '')

      const valueInCents = numericValue ? parseInt(numericValue, 10) : 0

      this.$emit('update:modelValue', valueInCents)
    },
    handleKeyDown(event) {
      const currentValue = this.modelValue || 0

      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (currentValue <= 0) {
          event.preventDefault()
        }
      }

      const allowedKeys = [
        '1', '2', '3', '4', '5', '6', '7', '8', '9',
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
      ]

      if (currentValue > 0) {
        allowedKeys.push('0')
      }

      if (!allowedKeys.includes(event.key)) {
        event.preventDefault()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.input-container {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  legend {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 100%;
    color: #606264;
  }

  .input {
    box-sizing: border-box;
    padding: 0.625rem;
    border: 1px solid #eaeaea;
    border-radius: 4px;
    outline: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 21.82px;

    &::placeholder {
      color: #d3d3d3;
    }

    &:disabled,
    &:read-only {
      cursor: not-allowed;
      background: #e6e6e6;
    }

    &:-webkit-autofill {
      box-shadow: 0 0 0px 1000px white inset !important;
      -webkit-box-shadow: 0 0 0px 1000px white inset !important;
    }
  }

  .password-container {
    box-sizing: border-box;
    height: 2.7375rem;
    border: 1px solid #eaeaea;
    border-radius: 4px;

    display: flex;
    align-items: center;

    .input {
      flex: 1;
      background: transparent;
      border: 0;
      outline: 0;
    }

    button {
      all: unset;
      padding-right: 0.625rem;
      cursor: pointer;
      color: #1c2d5a;
    }
  }

  &.variant-dark {
    >.input {
      border: 1px solid #707070;
    }

    .password-container {
      border: 1px solid #707070;
    }
  }

  .currency-container {
    padding-left: 0.5rem;
    box-sizing: border-box;
    height: 2.7375rem;
    border: 1px solid #EAEAEA;
    border-radius: 4px;

    display: flex;
    align-items: center;

    .input {
      flex: 1;
      background: transparent;
      border: 0;
      outline: 0;
    }

    span {
      font-weight: 800;
    }
  }
}
</style>
